export enum ScenarioType {
  SCENARIO_1_SALES_NO_QA = 'scenario_1',
  SCENARIO_2_SALES_IC_FRUIT = 'scenario_2',
  SCENARIO_3_SALES_IC_SHIPPING = 'scenario_3',
  SCENARIO_4_SALES_LOCAL = 'scenario_4',
  SCENARIO_5_SALES_VENDOR = 'scenario_5',
  SCENARIO_6_SALES_DISCOUNT = 'scenario_6',
  SCENARIO_7_CUSTOMER = 'scenario_7',
  SCENARIO_8_QA_FRUIT = 'scenario_8',
  SCENARIO_9_QA_SHIPPING = 'scenario_9',
  SCENARIO_10_INLAND = 'scenario_10',
  SCENARIO_11_DM_MARINE = 'scenario_11'
}

// Aligned with backend ClaimStatusTypes
export enum ClaimStatus {
  DRAFT = 1,
  SUBMITTED = 2,
  PENDING = 3,
  IN_PROGRESS = 4,
  APPROVED = 5,
  COMPLETE = 6,
  REJECTED = 7,
  CLOSED = 8,
  CUSTOMER_CLARIFICATION = 9,
  APPROVAL_CLARIFICATION = 10,
  PENDING_FOR_PAYMENT = 11,
  APPROVED_FOR_PAYMENT = 12,
  OPEN = 13,
  CLARIFICATION_REQUIRED = 14
}

/** Get display label for claim status (for lists, badges, etc.) */
export function getClaimStatusLabel(statusId: ClaimStatus | number): string {
  const labels: Record<number, string> = {
    1: 'Draft',
    2: 'Submitted',
    3: 'Pending',
    4: 'In Progress',
    5: 'Approved',
    6: 'Complete',
    7: 'Rejected',
    8: 'Closed',
    9: 'Customer Clarification',
    10: 'Approval Clarification',
    11: 'Pending for Payment',
    12: 'Approved for Payment',
    13: 'Open',
    14: 'Clarification Required'
  };
  return labels[statusId as number] ?? 'Unknown';
}

// Aligned with backend ClaimSeverityTypes
export enum ClaimSeverity {
  MAJOR = 1,
  MINOR = 2
}

export enum WorkflowStage {
  CREATION = 'creation',
  QA_REVIEW = 'qa_review',
  PROCUREMENT_REVIEW = 'procurement_review',
  SURVEYOR_ASSIGNMENT = 'surveyor_assignment',
  NWS_REVIEW = 'nws_review',
  FAD_APPROVAL = 'fad_approval',
  FINANCE_PROCESSING = 'finance_processing',
  CLOSED = 'closed'
}

export enum CreatorType {
  DM_SALES = 'dm_sales',
  MARKET_QA = 'market_qa',
  DM_MARINE = 'dm_marine',
  INLAND = 'inland',
  CUSTOMER = 'customer'
}

export enum UserRole {
  DM_SALES = 'dm_sales',
  MARKET_QA = 'market_qa',
  PROCUREMENT = 'procurement',
  FAD = 'fad',
  FINANCE = 'finance',
  SURVEYOR = 'surveyor',
  SURVEYOR_ASSIGNMENT = 'surveyor_assignment',
  RHO = 'rho',
  NWS = 'nws',
  SOURCE_QA = 'source_qa',
  DM_MARINE = 'dm_marine',
  INLAND = 'inland',
  CUSTOMER = 'customer'
}

export enum ItemType {
  INTERCOMPANY = 'intercompany',
  THIRD_PARTY_VENDOR = '3rd_party_vendor',
  DISCOUNT = 'discount'
}

export enum ProblemType {
  IC_FRUIT_RELATED = 'ic_fruit_related',
  SHIPPING_LINE = 'shipping_line',
  LOCAL_STORAGE = 'local_storage',
  VENDOR_ISSUE = 'vendor_issue',
  OTHERS = 'others'
}

/**
 * Map backend login response role id to claim UserRole.
 * Used so claim config (create/edit/view, isUserCreator) follows the currently selected role.
 */
export const BACKEND_ROLE_ID_TO_USER_ROLE: Partial<Record<number, UserRole>> = {
  2: UserRole.DM_SALES,       // DMSale
  3: UserRole.MARKET_QA,      // MarketPortQA
  4: UserRole.PROCUREMENT,    // Procurment
  5: UserRole.RHO,            // RegionalHeadOffice
  6: UserRole.INLAND,         // InlandINTRO
  7: UserRole.FAD,            // Approver
  8: UserRole.SOURCE_QA,       // DMSourceQA_Opreations
  9: UserRole.FINANCE,        // Finance
  10: UserRole.SURVEYOR_ASSIGNMENT,      // SurveyorAssignment
  11: UserRole.DM_MARINE,     // DMMarine
  12: UserRole.NWS,           // NWS
  13: UserRole.SURVEYOR       // Surveyor
};

export function getUserRoleFromBackendRoleId(roleId: number | string | null | undefined): UserRole | null {
  if (roleId == null) return null;
  const id = typeof roleId === 'string' ? parseInt(roleId, 10) : roleId;
  return BACKEND_ROLE_ID_TO_USER_ROLE[id] ?? null;
}

/** Display labels for backend role ids (evidence table, etc.) */
export const BACKEND_ROLE_ID_TO_LABEL: Partial<Record<number, string>> = {
  2: 'DM Sales',
  3: 'Market Port QA',
  4: 'Procurement',
  5: 'Regional Head Office',
  6: 'Inland',
  7: 'Approver',
  8: 'Source QA',
  9: 'Finance',
  10: 'Surveyor Assignment',
  11: 'DM Marine',
  12: 'NWS',
  13: 'Surveyor',
};

export function getBackendRoleDisplayName(roleId: number | string | null | undefined): string {
  if (roleId == null || roleId === '') return '—';
  const id = typeof roleId === 'string' ? parseInt(roleId, 10) : roleId;
  if (!Number.isFinite(id)) return '—';
  return BACKEND_ROLE_ID_TO_LABEL[id] ?? `Role ${id}`;
}

/** ActionHistoryTypes */
export const ActionHistoryTypes = {
  Draft: 1,
  SubmittedByDmSales: 2,
  SubmittedByMarketPortQa: 3,
  ActionByMarketPortQa: 4,
  ActionByDmSourceQa: 5,
  ActionByProcurement: 6,
  ActionBySurveyAssignment: 7,
  ActionByRegionalHeadOffice: 8,
  ActionBySurveyor: 9,
  ClarificationRequiredByApprover: 10,
  ClarificationRequiredByMarketPortQa: 11,
  ApprovedByApprover: 12,
  RejectedByApprover: 13,
  ActionByFinance: 14,
  UploadSurveyReport: 15,
  ActionByDMMarine: 16,
  SettlementByDMMarine: 17,
  SubmittedByDmSalesDiscountInfo: 18,
  SubmittedByInland: 19,
  ClarificationRequiredByProcurement: 20,
  CloseClaimByProcurement: 21,
  ClosedByDmSales: 22,
  ClosedByRHO: 23,
  RejectedByDmSales: 24,
  ApproverReassigned: 25,
} as const;

/** Get display label for action history type (for Action History tab) */
export function getActionHistoryLabel(actionId: number | null | undefined): string {
  const labels: Record<number, string> = {
    1: 'Draft',
    2: 'Submitted by DM Sales',
    3: 'Submitted by Market Port QA',
    4: 'Action by Market Port QA',
    5: 'Action by DM Source QA',
    6: 'Action by Procurement',
    7: 'Action by Survey Assignment',
    8: 'Action by Regional Head Office',
    9: 'Action by Surveyor',
    10: 'Clarification Required by Approver',
    11: 'Clarification Required by Market Port QA',
    12: 'Approved by Approver',
    13: 'Rejected by Approver',
    14: 'Action by Finance',
    15: 'Upload Survey Report',
    16: 'Action by DM Marine',
    17: 'Settlement by DM Marine',
    18: 'Submitted by DM Sales Discount Info',
    19: 'Submitted by Inland',
    20: 'Clarification Required by Procurement',
    21: 'Close Claim by Procurement',
    22: 'Closed by DM Sales',
    23: 'Closed by RHO',
    24: 'Rejected by DM Sales',
    25: 'Reassigned by Admin'
  };
  return (actionId != null ? labels[Number(actionId)] : null) ?? 'Unknown';
}